import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { fetchAsync } from "../services/ApiHandler";
import { Account } from "../model/Account";
import { Subscription } from "../model/Subscription";
import { MyPageMenu } from "../parts/MyPageMenu";
import { Inspiration } from "../model/Inspiration";
import { ModelTaste } from "../model/ModelTaste";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { InspirationKeyword } from "../model/InspirationKeyword";
import { Modal } from "@mantine/core";
import toast, { Toaster } from "react-hot-toast";
import { InspirationDialog } from "../parts/InspirationDialog";
import { useDisclosure } from "@mantine/hooks";
import { Helmet } from "react-helmet";
import _debounce from 'lodash/debounce';


export const InspirationPage: FC = () => {
  const [listOfList, setListOfList] = useState<Inspiration[][]>([]);
  const [allSampleList, setAllSampleList] = useState<Inspiration[]>([]);
  const [sampleList, setSampleList] = useState<Inspiration[]>([]);
  const [keywordList, setKeywordList] = useState<InspirationKeyword[]>([]);
  const [keyword, setKeyword] = useState<string>('');

  const [mainTasteList, setMainTasteList] = useState<ModelTaste[]>([]);
  const [sample, setSample] = useState<Inspiration | undefined>(undefined);
  const [index, setIndex] = useState<number>(1);
  const [inspirationDialogOpened, { open: openInspirationDialog, close: closeInspirationDialog }] = useDisclosure(false);
  const [processing, setProcessing] = useState(false);

  // const isLoading = useMemo(() => processing || index > 1, [processing, index]);


  useEffect(() => {
    async function fetchMainTasteList (){
      const list = await fetchAsync<ModelTaste[]>('ModelTaste/GetList');
      setMainTasteList(list);
    };

    async function fetchKeywordList (){
      const list = await fetchAsync<InspirationKeyword[]>('Inspiration/GetKeywordList');
      setKeywordList(list);
    };

    fetchKeywordList();
    fetchMainTasteList();
    fetchList();
  }, [])

  const fetchList = async () => {
    if(processing) return;

    setProcessing(true);
    var params = {
      page: index,
      count: 1000,
    };
    const response = await fetchAsync<Inspiration[]>('Inspiration/GetList', params);

    // response.sort(() => Math.random() - 0.5);
    // var newList = [...allSampleList, ...response];

    setAllSampleList(response);
    setSampleList(response);
    setProcessing(false);

    const slicedList = response.slice(index * 50, (index + 1) * 50);
    setListOfList(prev => [...prev, slicedList]);
    // setListOfList([...listOfList, newList]);
  };

  const setNextList = () => {
    const slicedList = allSampleList.slice(index * 50, (index + 1) * 50);
    setListOfList(prev => [...prev, slicedList]);
  }

  useEffect(() => {
    setNextList();
  }, [index]);

  const debounceFn = useCallback(_debounce(() => {
    console.log(`画面下までスクロールしました！`);
    setIndex(prev => prev + 1);
  }, 500), []);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const fullHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 200 >= fullHeight && !processing) {
      console.log('debounce')
      debounceFn();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    onChangeKeyword();
  },[keyword]);

  function onChangeKeyword() {
    if (keyword === '') {
      setSampleList(allSampleList);
      return;
    }

    var list = keywordList.filter((e) => e.keyword.startsWith(keyword));
    
    var newList: Inspiration[] = [];
    for(var sample of allSampleList) {
      var selectedList = list.filter((e) => e.inspirationNumber === sample.inspirationNumber);
      if(selectedList.length === 1) {
        newList.push(sample);
      }
    }

    setSampleList(newList);
  }

  function onSelectSample(sample: Inspiration) {
    setSample(sample);
    openInspirationDialog();
  }

  function buildDialog(){
    return <>
    <Modal size="auto" opened={inspirationDialogOpened} onClose={() => closeInspirationDialog()}>
      <InspirationDialog inspiration={sample} mainTasteList={mainTasteList} />
    </Modal>
    </>;
  }

  return <>
    { sample && buildDialog() }
    <Helmet>
      <title>インスピレーション - Drop AI</title>
      <meta name="description" content="インスピレーションページの豊富な画像コレクションから画像生成のヒントやプロンプトを探そう" />
      <meta property="og:url" content="https://drop4i.com/inspiration" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="インスピレーション - Drop AI | 無料で使えるAI画像生成ツール" />
      <meta property="og:description" content="インスピレーションページの豊富な画像コレクションから画像生成のヒントやプロンプトを探そう" />
    </Helmet>
    <Toaster />
    <div className="">
      <div className="mt-12 m-auto max-w-[1200px] bg-white"> 
        <div className="text-center py-4">
          <img src="/images/symbol-drop.svg" className="w-[60px] text-center center" />
          <div className="mt-4">
            <h2 className="text-[24px] font-bold gradation-text">クリエイティブインスピレーションを得よう</h2>
          </div>
          <div className="mt-2 mb-8 px-4">
            <span className="text-[16px]">画像コレクションから構成ヒントや参考プロンプトを探そう</span>
          </div>
        </div>

        <div className="min-h-[calc(100vh-76px)] mb-12">
          <div className="text-center mb-10 relative w-full sm:w-[480px] mx-auto px-4">
            <input value={keyword} className="border rounded  w-full sm:w-[480px] mb-4 pl-8 py-1" onChange={(e) => setKeyword(e.target.value)} placeholder="プロンプトを検索" />
            <img src="/images/icon/icon-search.svg" className="w-[20px] h-[20px] absolute left-6 top-2" />
          </div>

          { listOfList.map((e, index) => (
            <>
            { e.length > 0 && (
              <div className="cards overflow-x-auto sm:max-w-[calc(100vw-230px)] max-w-[100vw] mx-auto px-4">
              { e.map((e) => (
                <div className="card">
                  <img onClick={() => onSelectSample(e)} className="" loading="lazy" style={{ borderRadius: "8px"}} src={`${process.env.REACT_APP_IMAGE_URL}/sample/${e.imageFileName.replace(".jpeg", ".jpeg")}`} />
                </div>
                  ))}
                </div>
              )}
            </>
          ))} 
        </div>
      </div>
    </div>
  </>;
}
  